import {
    AuthError,
    createUserWithEmailAndPassword,
    fetchSignInMethodsForEmail,
    GoogleAuthProvider,
    onIdTokenChanged,
    reload,
    sendEmailVerification,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithPopup,
    User,
    UserCredential,
} from 'firebase/auth';
import { auth } from 'src/firebase';
import { HoopsAuth } from './models/HoopsAuth';
import { REQUIRE_EMAIL_VERIFICATION } from './HoopsAuthConfig';

const GOOGLE_PROVIDER_ID = 'google.com';
const PASSWORD_PROVIDER_ID = 'password';

/**
 * After clicking the verification link in their inbox, the user is bounced
 * back to `/verify-email` on the origin they started from. That page auto-
 * polls `reload()` and forwards them on once `emailVerified` flips, so the
 * round-trip from "open inbox" to "back in the app, signed in and verified"
 * happens with no extra clicks. Deriving from `window.location.origin` keeps
 * the same Firebase project working across prod (bostonhoops.org) and local
 * dev (localhost:3000) without environment-specific config.
 *
 * The origin must be in Authentication → Settings → Authorized domains, or
 * Firebase rejects the call with `auth/unauthorized-continue-uri`. `localhost`
 * is allow-listed by Firebase by default.
 */
const emailVerificationActionSettings = () => ({
    url: `${window.location.origin}/verify-email`,
    handleCodeInApp: false,
});

export type HoopsAuthProviderId = 'google.com' | 'password';

/**
 * Thrown for any expected auth failure surfaced from `HoopsAuthService`.
 *
 * The `code` discriminates the case so callers (typically the sign-in form)
 * can render the appropriate inline message. All raw Firebase errors are
 * mapped into one of these — application code should never need to inspect
 * the underlying Firebase error codes.
 */
export class HoopsAuthError extends Error {
    readonly code: HoopsAuthErrorCode;
    /**
     * When the user attempted one provider but an account already exists for the
     * same email under a different provider, this names that other provider.
     */
    readonly existingProvider?: HoopsAuthProviderId;

    constructor(code: HoopsAuthErrorCode, message: string, existingProvider?: HoopsAuthProviderId) {
        super(message);
        this.name = 'HoopsAuthError';
        this.code = code;
        this.existingProvider = existingProvider;
    }
}

export type HoopsAuthErrorCode =
    | 'invalid-credentials'          // wrong password, or no such email
    | 'invalid-email'                // malformed email
    | 'weak-password'                // password too short / not accepted by Firebase
    | 'email-already-in-use'         // sign-up: email is already a password account
    | 'wrong-provider'               // email exists but under a different provider
    | 'popup-closed'                 // user closed the Google popup
    | 'too-many-requests'            // Firebase throttled the client
    | 'network'                      // offline / fetch failed
    | 'unknown';                     // catch-all

/**
 * `HoopsAuthService` is the single entry point for everything related to the
 * Firebase Auth user session.
 *
 * Two sign-in providers are supported and treated as peers:
 *  - Google (popup)
 *  - Email/Password
 *
 * Password sign-in is by **email** (`signInWithEmailAndPassword`).
 *
 * ### Cross-provider conflicts
 *
 * Firebase projects use the default "one account per email" setting, which
 * means an email can be tied to only one provider. The two relevant
 * conflict cases are:
 *
 *  1. **User signed up with Google, then tries email/password.** Either the
 *     password sign-in fails (`auth/invalid-credential`) or the password
 *     sign-up fails (`auth/email-already-in-use`). We probe with
 *     `fetchSignInMethodsForEmail` to detect this and throw a
 *     `wrong-provider` error naming Google so the form can prompt the user
 *     to click the Google button.
 *  2. **User signed up with email/password, then tries Google.** The popup
 *     sign-in fails with `auth/account-exists-with-different-credential`.
 *     Same treatment — we throw `wrong-provider` naming `password`.
 *
 * If Firebase Email Enumeration Protection is enabled, the lookup methods
 * may return empty results; in that case we fall through to a generic
 * `invalid-credentials` or `email-already-in-use` error rather than guessing.
 */
export class HoopsAuthService {

    /**
     * Subscribes to Firebase Auth state changes (sign-in/out and ID-token
     * refreshes, so custom-claim changes propagate). Wraps the raw `User` in a
     * `HoopsAuth` model, or yields `null` when signed out.
     * @param onNextAuth Callback invoked on every auth state change
     * @returns Unsubscribe function
     */
    public onAuthChange(onNextAuth: (auth: HoopsAuth | null) => void) {
        // toHoopsAuth is async (claims read); the ticket drops a slow resolve
        // for an older auth state so it can't overwrite a newer one.
        let latest = 0;
        return onIdTokenChanged(auth, async (user) => {
            const ticket = ++latest;
            const next = user === null ? null : await this.toHoopsAuth(user);
            if (ticket === latest) {
                onNextAuth(next);
            }
        });
    }

    /**
     * Re-fetches the current user from Firebase so `emailVerified` reflects the
     * server state, then returns the refreshed `HoopsAuth`. Returns null if no
     * user is signed in. Required because `onAuthStateChanged` does not refire
     * when a user verifies their email out-of-band (clicking the email link).
     */
    public async reloadCurrentAuth(): Promise<HoopsAuth | null> {
        const user = auth.currentUser;
        if (!user) return null;
        await reload(user);
        return this.toHoopsAuth(user);
    }

    /**
     * Forces a new ID token (picking up any custom-claim changes, e.g. a just-
     * granted admin role) and returns the refreshed `HoopsAuth`. The refresh
     * also refires `onAuthChange`, so provider state updates automatically.
     * Returns null if no user is signed in.
     */
    public async refreshClaims(): Promise<HoopsAuth | null> {
        const user = auth.currentUser;
        if (!user) return null;
        await user.getIdToken(true);
        return this.toHoopsAuth(user);
    }

    /**
     * Sends (or resends) the email-verification message to the currently
     * signed-in user. No-ops silently if nobody is signed in.
     */
    public async sendVerificationEmail(): Promise<void> {
        const user = auth.currentUser;
        if (!user) return;
        try {
            await sendEmailVerification(user, emailVerificationActionSettings());
        } catch (err) {
            throw this.mapGenericError(err);
        }
    }

    /**
     * Signs in with Google via popup. If the email is already attached to a
     * password account, throws `HoopsAuthError({ code: 'wrong-provider',
     * existingProvider: 'password' })` so the form can tell the user to sign
     * in with their password instead.
     */
    public async signInWithGoogle(): Promise<UserCredential> {
        const provider = new GoogleAuthProvider();
        try {
            return await signInWithPopup(auth, provider);
        } catch (err) {
            throw this.mapPopupError(err);
        }
    }

    /** Signs in with an email + password. */
    public async signInWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
        const trimmed = email.trim();
        try {
            return await signInWithEmailAndPassword(auth, trimmed, password);
        } catch (err) {
            throw await this.mapPasswordSignInError(err, trimmed);
        }
    }

    /**
     * Creates a new email/password account. Does not create the HoopsUser
     * profile document — that happens on the profile-creation page after
     * sign-in completes (so the same flow handles Google and password users
     * uniformly).
     */
    public async signUpWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
        const trimmed = email.trim();
        let credential: UserCredential;
        try {
            credential = await createUserWithEmailAndPassword(auth, trimmed, password);
        } catch (err) {
            throw await this.mapPasswordSignUpError(err, trimmed);
        }
        // Fire-and-don't-fail: account creation succeeded, so don't reject the
        // sign-up just because the verification email couldn't be queued. The
        // verify-email screen has a resend button to recover.
        if (REQUIRE_EMAIL_VERIFICATION) {
            try {
                await sendEmailVerification(credential.user, emailVerificationActionSettings());
            } catch (err) {
                console.error('Failed to send verification email after sign-up:', err);
            }
        }
        return credential;
    }

    /**
     * Sends a password-reset email. Resolves successfully regardless of
     * whether the email is registered (Firebase Email Enumeration Protection
     * makes the response opaque) — the form should always show the same
     * "check your inbox" confirmation.
     */
    public async sendPasswordReset(email: string): Promise<void> {
        try {
            await sendPasswordResetEmail(auth, email.trim());
        } catch (err) {
            throw this.mapGenericError(err);
        }
    }

    /**
     * Signs the current user out. Always resolves.
     */
    public signOut(): Promise<void> {
        return auth.signOut();
    }

    // --- internals ---------------------------------------------------------

    private async toHoopsAuth(user: User): Promise<HoopsAuth> {
        const token = await user.getIdTokenResult();
        return new HoopsAuth(
            user.uid,
            user.displayName,
            user.photoURL,
            user.email,
            user.emailVerified,
            token.claims.admin === true,
        );
    }

    private async mapPasswordSignInError(err: unknown, email: string): Promise<HoopsAuthError> {
        const code = this.firebaseErrorCode(err);
        switch (code) {
            case 'auth/invalid-email':
                return new HoopsAuthError('invalid-email', 'That email address looks invalid.');
            case 'auth/user-disabled':
                return new HoopsAuthError('invalid-credentials', 'This account has been disabled.');
            case 'auth/too-many-requests':
                return new HoopsAuthError('too-many-requests',
                    'Too many sign-in attempts. Please wait a moment and try again.');
            case 'auth/network-request-failed':
                return new HoopsAuthError('network', 'Network error. Please check your connection and try again.');
            case 'auth/invalid-credential':
            case 'auth/wrong-password':
            case 'auth/user-not-found': {
                // Could be wrong password, or the account is Google-only. Probe to find out.
                const otherProvider = await this.detectOtherProvider(email, PASSWORD_PROVIDER_ID);
                if (otherProvider) {
                    return new HoopsAuthError('wrong-provider',
                        this.wrongProviderMessage(otherProvider), otherProvider);
                }
                return new HoopsAuthError('invalid-credentials',
                    'Invalid email or password.');
            }
            default:
                return this.mapGenericError(err);
        }
    }

    private async mapPasswordSignUpError(err: unknown, email: string): Promise<HoopsAuthError> {
        const code = this.firebaseErrorCode(err);
        switch (code) {
            case 'auth/invalid-email':
                return new HoopsAuthError('invalid-email', 'That email address looks invalid.');
            case 'auth/weak-password':
                return new HoopsAuthError('weak-password',
                    'Password is too weak. Please choose at least 6 characters.');
            case 'auth/network-request-failed':
                return new HoopsAuthError('network', 'Network error. Please check your connection and try again.');
            case 'auth/email-already-in-use': {
                // If the existing account is Google, route the user there.
                const otherProvider = await this.detectOtherProvider(email, PASSWORD_PROVIDER_ID);
                if (otherProvider) {
                    return new HoopsAuthError('wrong-provider',
                        this.wrongProviderMessage(otherProvider), otherProvider);
                }
                return new HoopsAuthError('email-already-in-use',
                    'An account with this email already exists. Try signing in instead.');
            }
            default:
                return this.mapGenericError(err);
        }
    }

    private mapPopupError(err: unknown): HoopsAuthError {
        const code = this.firebaseErrorCode(err);
        switch (code) {
            case 'auth/popup-closed-by-user':
            case 'auth/cancelled-popup-request':
                return new HoopsAuthError('popup-closed', 'Sign-in was cancelled.');
            case 'auth/popup-blocked':
                return new HoopsAuthError('popup-closed',
                    'Your browser blocked the sign-in popup. Please allow popups and try again.');
            case 'auth/network-request-failed':
                return new HoopsAuthError('network', 'Network error. Please check your connection and try again.');
            case 'auth/account-exists-with-different-credential': {
                // The user has a password account for this email — point them at it.
                return new HoopsAuthError('wrong-provider',
                    this.wrongProviderMessage(PASSWORD_PROVIDER_ID), PASSWORD_PROVIDER_ID);
            }
            default:
                return this.mapGenericError(err);
        }
    }

    private mapGenericError(err: unknown): HoopsAuthError {
        const code = this.firebaseErrorCode(err);
        const message = (err instanceof Error && err.message) || 'Something went wrong. Please try again.';
        return new HoopsAuthError('unknown', code ? `${message} (${code})` : message);
    }

    /**
     * Returns the first provider attached to `email` that isn't `excludeProvider`,
     * or null if we can't tell. Used to surface "wrong provider" conflicts.
     * If email enumeration protection is on, Firebase returns an empty list
     * and we fall back to null (caller will show a generic message).
     */
    private async detectOtherProvider(
        email: string,
        excludeProvider: HoopsAuthProviderId
    ): Promise<HoopsAuthProviderId | null> {
        try {
            const methods = await fetchSignInMethodsForEmail(auth, email);
            for (const m of methods) {
                if (m === GOOGLE_PROVIDER_ID && excludeProvider !== GOOGLE_PROVIDER_ID) return GOOGLE_PROVIDER_ID;
                if (m === PASSWORD_PROVIDER_ID && excludeProvider !== PASSWORD_PROVIDER_ID) return PASSWORD_PROVIDER_ID;
            }
            return null;
        } catch {
            return null;
        }
    }

    private wrongProviderMessage(provider: HoopsAuthProviderId): string {
        if (provider === GOOGLE_PROVIDER_ID) {
            return 'This account was created with Google. Please use the "Sign in with Google" button below.';
        }
        return 'This account was created with a password. Please sign in with your email and password.';
    }

    private firebaseErrorCode(err: unknown): string | null {
        if (err && typeof err === 'object' && 'code' in err && typeof (err as AuthError).code === 'string') {
            return (err as AuthError).code;
        }
        return null;
    }
}

export const hoopsAuthService = new HoopsAuthService();
